import React, { useEffect, useState, useRef } from 'react';
import { getUsers } from '../api';

export default function UserSelector({ selected = [], onChange }) {
  const [users,  setUsers]  = useState([]);
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    getUsers().then(res => {
      // Deduplicate AI agents — keep only one
      const seen = new Set();
      const unique = res.data.filter(u => {
        if (u.gpf_is_ai_agent) {
          if (seen.has('ai')) return false;
          seen.add('ai');
        }
        return true;
      });
      setUsers(unique);
    }).catch(console.error);
  }, []);

  // ── Close on click outside ──
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (userId) => {
    const id = parseInt(userId);
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
  };

  const selectedUsers = users.filter(u => selected.includes(u.id));
  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const initials = (name) => (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      <div style={styles.selectedRow}>
        {selectedUsers.map(u => (
          <div key={u.id} style={{ ...styles.avatar, background: u.gpf_is_ai_agent ? '#7c3aed' : '#2563eb', fontSize: u.gpf_is_ai_agent ? 14 : 11 }} title={u.name}>
            {u.gpf_is_ai_agent ? '🤖' : initials(u.name)}
          </div>
        ))}
        <button type="button" style={styles.addBtn} onClick={() => setOpen(!open)}>
          {open ? '✕ Close' : '+ Assign'}
        </button>
      </div>

      {open && (
        <div style={styles.dropdown}>
          <input
            style={styles.search}
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <div style={styles.list}>
            {filtered.length === 0 ? (
              <p style={styles.empty}>No users found.</p>
            ) : (
              filtered.map(u => {
                const isSelected = selected.includes(u.id);
                return (
                  <div
                    key={u.id}
                    style={{ ...styles.userItem, background: isSelected ? '#eff6ff' : '#fff' }}
                    onClick={() => toggle(u.id)}
                  >
                    <div style={{ ...styles.itemAvatar, background: u.gpf_is_ai_agent ? '#7c3aed' : isSelected ? '#2563eb' : '#94a3b8', fontSize: u.gpf_is_ai_agent ? 16 : 11 }}>
                      {u.gpf_is_ai_agent ? '🤖' : initials(u.name)}
                    </div>
                    <div style={styles.userInfo}>
                      <span style={{ ...styles.userName, color: u.gpf_is_ai_agent ? '#7c3aed' : '#0f172a' }}>{u.name || u.slug}</span>
                      <span style={styles.userRole}>{u.gpf_is_ai_agent ? 'AI Agent' : (u.roles?.[0]?.replace('gpf_', '').replace(/_/g, ' ') || 'user')}</span>
                    </div>
                    {isSelected && <span style={styles.checkmark}>✓</span>}
                  </div>
                );
              })
            )}
          </div>
          <div style={styles.footer}>
            <button type="button" style={styles.doneBtn} onClick={() => { setOpen(false); setSearch(''); }}>
              Done ({selected.length} selected)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper:     { position: 'relative' },
  selectedRow: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  avatar:      { width: 30, height: 30, borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, cursor: 'default', border: '2px solid #fff', boxShadow: '0 0 0 1px #e2e8f0' },
  addBtn:      { padding: '4px 10px', background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#64748b', cursor: 'pointer' },
  dropdown:    { position: 'absolute', top: '100%', left: 0, zIndex: 200, background: '#fff', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', width: 260, marginTop: 6 },
  search:      { width: '100%', padding: '10px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 13, outline: 'none', borderRadius: '10px 10px 0 0', boxSizing: 'border-box' },
  list:        { maxHeight: 200, overflowY: 'auto' },
  empty:       { padding: '12px 16px', color: '#94a3b8', fontSize: 13, margin: 0 },
  userItem:    { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', transition: 'background 0.1s' },
  itemAvatar:  { width: 28, height: 28, borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 },
  userInfo:    { flex: 1, minWidth: 0 },
  userName:    { fontSize: 13, fontWeight: 600, display: 'block' },
  userRole:    { fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' },
  checkmark:   { color: '#2563eb', fontWeight: 700, fontSize: 14 },
  footer:      { padding: '8px 12px', borderTop: '1px solid #f1f5f9' },
  doneBtn:     { width: '100%', padding: '7px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};